// Patterns for Airbrake notices we deliberately do NOT report.
//
// These are confirmed noise from production analysis (2026-07-10): they are
// either non-actionable (React hydration mismatches, malformed adapter errors),
// third-party code we don't control (browser extensions), or bot/scanner probes.
// Real, actionable errors are intentionally left unfiltered.

// Matched against each error's `message`.
export const IGNORED_ERROR_PATTERNS: readonly string[] = [
  // React hydration mismatches — noisy and not actionable via Airbrake.
  // (#418 alone was ~496k notices in the last 30 days.)
  'Minified React error #418',
  'Minified React error #423',
  'Minified React error #425',
  // Malformed error with no actionable payload (XHR object stringified).
  '[object XMLHttpRequest]',
  // Known ASP-style scanner/bot probe, not a real client error.
  'Object Not Found Matching Id',
]

// Matched against each backtrace frame's `file`. These schemes indicate the
// error originated in a browser extension / injected third-party code, never
// our bundle, so it is not our error to track.
export const IGNORED_SOURCE_PATTERNS: readonly string[] = [
  'safari-web-extension://',
  'chrome-extension://',
  'moz-extension://',
  // NOTE: do NOT add 'webkit-masked-url://' here. WebKit uses that scheme to
  // mask the URLs of our OWN first-party scripts in Safari/iOS stack traces —
  // it is not an extension indicator. Filtering it drops real iOS errors.
]

type NoticeLike = {
  errors?: Array<{
    message?: string
    backtrace?: Array<{ file?: string }>
  }>
}

/**
 * Returns true when a notice should be dropped (not sent to Airbrake).
 * A notice is ignored if any of its errors matches an ignored message
 * pattern, or if any backtrace frame comes from an ignored source scheme.
 */
export function shouldIgnoreNotice(notice: NoticeLike): boolean {
  const errors = notice.errors ?? []
  return errors.some((err) => {
    const message = err.message ?? ''
    if (IGNORED_ERROR_PATTERNS.some((pattern) => message.includes(pattern))) {
      return true
    }
    const frames = err.backtrace ?? []
    return frames.some((frame) =>
      IGNORED_SOURCE_PATTERNS.some((pattern) => (frame.file ?? '').includes(pattern)),
    )
  })
}
